<template>
  <div
    class="dp-selected-item"
    :class="[
      `dp-selected-item--${layout}`,
      {
        'dp-selected-item--full': fullWidth,
        'dp-selected-item--embedded': embedded,
        'dp-selected-item--error': isUploadedFile && file.status === 'error',
      },
    ]"
    :style="rootStyle"
    :data-locator="dataLocator"
  >
    <div class="dp-selected-item__left">
      <ClosdFildersIcon
        :document="file"
        :size="computedIconSize"
        class="dp-selected-item__icon"
      />
    </div>

    <AppTooltip location="top">
      <template #activator="{ props }">
        <span
          v-bind="props"
          class="dp-selected-item__label text-truncate"
        >
          <span v-if="file.numbering" class="mr-1">{{ file.numbering }}</span>
          {{ file.name || file.basename }}
        </span>
      </template>

      {{ file.name || file.basename }}
    </AppTooltip>

    <template v-if="isUploadedFile">
      <div v-if="file.status === 'pending'" class="d-flex">
        <app-material-icon
          class="mx-2 spinning"
          name="progress_activity"
          size="sm"
        />
        <span class="dp-selected-item__upload-infos">{{ $t('common.DocumentsPickerSelectedItem.uploading') }}</span>
      </div>


      <app-filesize-display
        v-if="file.status === 'success'"
        class="dp-selected-item__upload-infos"
        :value="file.size"
      />

      <div
        v-if="file.status === 'error'"
        class="d-flex align-center dp-selected-item__upload-infos dp-selected-item__upload-infos--error"
      >
        <app-material-icon
          class="mr-2"
          name="emergency_home"
          size="sm"
          color="currentColor"
        />
        <span>{{ $t('common.msgFailErrorOccurred') }}</span>
      </div>

      <v-divider
        vertical
        color="grey-100"
        opacity="1"
        length="24"
        class="ma-auto"
      />
    </template>

    <AppTooltip
      v-if="canRemove"
      location="top"
    >
      <template #activator="{ props }">
        <app-button
          v-bind="props"
          type="icon"
          size="x-small"
          variant="neutral"
          class="dp-selected-item__remove blur-editor-exception"
          @click.stop="$emit('remove', file)"
        >
          <template #left-icon>
            <app-material-icon
              name="close"
              size="sm"
            />
          </template>
        </app-button>
      </template>

      {{ $t('common.deselect') }}
    </AppTooltip>
  </div>
</template>

<script>
import AppTooltip from '@/common/AppTooltip.vue'
import ClosdFildersIcon from '@/common/filders/ClosdFildersIcon.vue'

export default {
  name: 'DocumentsPickerSelectedItem',
  components: { ClosdFildersIcon, AppTooltip },
  props: {
    file: {
      type: Object,
      required: true,
    },
    layout: {
      type: String,
      default: 'block',
      validator: (v) => ['block', 'inline'].includes(v),
    },
    fullWidth: {
      type: Boolean,
      default: false,
    },
    embedded: {
      type: Boolean,
      default: false,
    },
    maxWidth: {
      type: Number,
      default: 320,
    },
    iconSize: {
      type: Number,
      default: null,
    },
    locatorPrefix: {
      type: String,
      default: 'picker-selected',
    },
    canRemove: {
      type: Boolean,
      default: true,
    },
  },
  emits: ['remove'],
  computed: {
    computedIconSize () {
      if (this.iconSize) return this.iconSize
      return this.layout === 'inline' ? 16 : 18
    },
    dataLocator () {
      return `${this.locatorPrefix}-item-${this.file.id}`
    },
    rootStyle () {
      if (this.fullWidth) return { maxWidth: 'none' }
      if (this.layout !== 'inline') return null
      return { maxWidth: `${this.maxWidth}px` }
    },
    isUploadedFile () {
      return !!this.file.status
    },
  },
}
</script>

<style scoped lang="scss">
.dp-selected-item {
  height: 56px;
  min-height: 56px;
  max-height: 56px;
  border: 1px solid rgb(var(--v-theme-grey-100));
  border-radius: 10px;
  background: rgb(var(--v-theme-grey-25));
  padding: 0 14px;
  display: inline-flex;
  align-items: center;
  gap: 12px;
  min-width: 0;

  &--error {
    border: 2px solid rgb(var(--v-theme-error));
  }
}

.dp-selected-item--embedded {
  height: auto;
  min-height: 0;
  max-height: none;
  border: 0;
  border-radius: 0;
  background: transparent;
  padding: 0;
  width: 100%;
}

.dp-selected-item__left {
  width: 22px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
}

.dp-selected-item__icon {
  flex: 0 0 auto;
}

.dp-selected-item__label {
  flex: 1 1 auto;
  min-width: 0;
  color: rgb(var(--v-theme-grey-900));
  font-size: 14px;
}

.dp-selected-item__upload-infos {
  color: rgb(var(--v-theme-grey-700));
  opacity: 1;
  font-size: 12px;
  text-wrap: nowrap;

  &--error {
    color: rgb(var(--v-theme-error));
  }
}

.dp-selected-item__remove {
  flex: 0 0 auto;
}

.dp-selected-item--block {
  width: 100%;
}

.dp-selected-item--inline {
  flex: 0 0 auto;
}

.dp-selected-item--full {
  width: 100%;
  max-width: none !important;
}
</style>
